import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import GraficosBasicos from "../../components/solicitacoes/Dashboard/GraficosBasicos";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import SolicitacaoPessoalService from "../../services/resource/solicitacaoPessoalService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { kpisBasicos } from "../../utils/solicitacoes/chartOptions";

const service = new SolicitacaoPessoalService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));

const MOTIVO_VAGA_LABELS = { AUMENTO_QUADRO: "Aumento de Quadro", SUBSTITUICAO: "Substituição" };
const TIPO_SELECAO_LABELS = { INTERNA: "Interna", EXTERNA: "Externa" };
const MOTIVO_VAGA_OPTIONS = Object.entries(MOTIVO_VAGA_LABELS).map(([value, label]) => ({ value, label }));

const FILTROS_INICIAIS_EXTRA = { unidadeNome: "", cargoAPreencher: "", motivoVaga: null, status: null, solicitanteId: null };

const COLUNAS = [
    { key: "unidadeNome", label: "Filial" },
    { key: "motivoVaga", label: "Motivo", render: v => MOTIVO_VAGA_LABELS[v] || v || "-" },
    { key: "vagaSigilosa", label: "Sigilosa", render: v => (v ? "Sim" : "Não"), center: true },
    { key: "motivoSolicitacao", label: "Motivo da Solicitação" },
    { key: "cargoAPreencher", label: "Cargo a Preencher" },
    { key: "horario", label: "Horário" },
    { key: "adicionais", label: "Adicionais" },
    { key: "setor", label: "Setor" },
    { key: "cidadeTrabalho", label: "Cidade" },
    { key: "resumoAtribuicoes", label: "Resumo das Atribuições" },
    { key: "selecaoAprovadaInicio", label: "Seleção Aprovada", render: v => (v == null ? "-" : v ? "Sim" : "Não"), center: true },
    { key: "tipoSelecao", label: "Tipo de Seleção", render: v => TIPO_SELECAO_LABELS[v] || v || "-" },
    { key: "cargoPreenchido", label: "Cargo Preenchido" },
    { key: "area", label: "Área" },
    { key: "salario", label: "Salário", format: "currency" },
    { key: "dataAdmissao", label: "Data de Admissão", format: "date" },
    { key: "justificativaSelecaoNaoRealizada", label: "Justificativa (Seleção Não Realizada)" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data", format: "date" },
];

export default function RelatorioSolicitacaoPessoal() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.solicitacaoPessoal.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Solicitação de Pessoal (Funcionário)";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    return (
        <DashboardProcessoLayout
            titulo="Solicitação de Pessoal (Funcionário)"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={kpisBasicos(dashboard, "fa fa-user-plus")}
            graficos={<GraficosBasicos dashboard={dashboard} />}
            filtrosExtras={(
                <>
                    <Campo label="Filial">
                        <input type="text" value={filtros.unidadeNome} onChange={e => setFiltro("unidadeNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Cargo a Preencher">
                        <input type="text" value={filtros.cargoAPreencher} onChange={e => setFiltro("cargoAPreencher", e.target.value)}
                            placeholder="Buscar por cargo" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Motivo">
                        <CustomDropdown value={filtros.motivoVaga} onChange={e => setFiltro("motivoVaga", e.value)} options={MOTIVO_VAGA_OPTIONS}
                            placeholder="Todos os motivos" showClear />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportFilename="dashboard_solicitacao_pessoal"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
